import React from 'react';
import { ChevronDown } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import SubpageHeader from './SubpageHeader';
import { useReserveContext } from '../../contexts/useReserveContext';
import {
  fetchStoresForServiceReserve,
} from '../../utils/reservation/by-service/fetchStoresForServiceReserve';
import {
  fetchServicesForServiceReserve,
  type ServiceForServiceReserve,
} from '../../utils/reservation/by-service/fetchServicesForServiceReserve';
import { pickPreferredStore } from './homeShared';
import { useI18n } from '../../i18n';

type ServiceCategory = {
  id: number | null;
  name: string;
  colorClass: string | null;
  services: ServiceForServiceReserve[];
};

const COLOR_HEX_MAP: Record<string, string> = {
  slate: '#64748b',
  gray: '#6b7280',
  zinc: '#71717a',
  neutral: '#737373',
  stone: '#78716c',
  red: '#ef4444',
  orange: '#f97316',
  amber: '#f59e0b',
  yellow: '#eab308',
  lime: '#84cc16',
  green: '#22c55e',
  emerald: '#10b981',
  teal: '#14b8a6',
  cyan: '#06b6d4',
  sky: '#0ea5e9',
  blue: '#3b82f6',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  purple: '#a855f7',
  fuchsia: '#d946ef',
  pink: '#ec4899',
  rose: '#f43f5e',
};

const extractColorHex = (colorClass?: string | null) => {
  const source = String(colorClass ?? '');
  const matched = source.match(/(?:text|bg)-([a-z]+)-\d{2,3}/i);
  const family = matched?.[1]?.toLowerCase() ?? '';
  return COLOR_HEX_MAP[family] ?? '#cbd5e1';
};

const hexToRgba = (hex: string, alpha: number) => {
  const normalized = hex.replace('#', '');
  const full = normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized;
  const value = Number.parseInt(full, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatMoney = (value?: number | null) => {
  if (value == null || !Number.isFinite(Number(value))) return '-';
  return `$${Number(value).toFixed(2)}`;
};

const MenuCategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { categoryId } = useParams<{ categoryId?: string }>();
  const { storeId } = useReserveContext();
  const [loading, setLoading] = React.useState(true);
  const [categories, setCategories] = React.useState<ServiceCategory[]>([]);
  const [expandedKey, setExpandedKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const stores = await fetchStoresForServiceReserve();
        const preferredStore = pickPreferredStore(stores, storeId);

        if (!preferredStore) {
          if (!isMounted) return;
          setCategories([]);
          setExpandedKey(null);
          return;
        }

        const services = await fetchServicesForServiceReserve(preferredStore.id);
        const grouped = new Map<number | null, ServiceCategory>();

        services.forEach((service) => {
          const key = service.category_id ?? null;
          if (!grouped.has(key)) {
            grouped.set(key, {
              id: key,
              name: service.category_name?.trim() || t('menu.other'),
              colorClass: service.color_class ?? null,
              services: [],
            });
          }

          const current = grouped.get(key);
          if (current) {
            current.services.push(service);
            if (!current.colorClass && service.color_class) {
              current.colorClass = service.color_class;
            }
          }
        });

        if (!isMounted) return;
        const nextCategories = Array.from(grouped.values());
        setCategories(nextCategories);

        if (categoryId) {
          const matched = nextCategories.find((category) =>
            String(category.id == null ? 'other' : category.id) === categoryId,
          );
          setExpandedKey(matched ? String(matched.id == null ? 'other' : matched.id) : null);
        }
      } catch (error) {
        console.error(error);
        if (!isMounted) return;
        setCategories([]);
        setExpandedKey(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    void load();

    return () => {
      isMounted = false;
    };
  }, [storeId, categoryId, t]);

  const toggleCategory = (key: string) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  return (
    <div className="space-y-4">
      <SubpageHeader title={t('menu.title')} onBack={() => navigate('/reserve')} />

      {loading ? (
        <div className="rounded-[28px] border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500 shadow-sm">
          {t('menu.loading')}
        </div>
      ) : categories.length === 0 ? (
        <div className="rounded-[28px] border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500 shadow-sm">
          {t('menu.empty')}
        </div>
      ) : (
        <div className="space-y-3">
          {categories.map((category) => {
            const key = String(category.id == null ? 'other' : category.id);
            const isExpanded = expandedKey === key;
            const accent = extractColorHex(category.colorClass);

            return (
              <section
                key={key}
                className="relative overflow-hidden rounded-[28px] border shadow-[0_16px_38px_rgba(15,23,42,0.08)] transition-all duration-300"
                style={{
                  borderColor: isExpanded ? hexToRgba(accent, 0.34) : hexToRgba(accent, 0.18),
                  background: isExpanded
                    ? `linear-gradient(180deg, ${hexToRgba(accent, 0.18)}, rgba(255,255,255,0.96))`
                    : hexToRgba(accent, 0.12),
                }}
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-5 top-0 h-12 rounded-b-[24px] bg-[linear-gradient(180deg,rgba(255,255,255,0.72),rgba(255,255,255,0.08))]"
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-24"
                  style={{
                    background: `linear-gradient(180deg, rgba(255,255,255,0), ${hexToRgba(accent, 0.14)})`,
                  }}
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-10 top-5 h-28 w-28 rounded-full bg-white/40 blur-2xl"
                />
                <button
                  type="button"
                  onClick={() => toggleCategory(key)}
                  className="relative w-full px-5 py-5 text-left"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <span
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: accent }}
                        />
                        <div className="min-w-0 text-lg font-semibold tracking-[-0.02em] text-slate-950">
                          {category.name}
                        </div>
                      </div>
                      <div className="mt-2 text-sm font-medium text-slate-500">
                        {category.services.length} {category.services.length === 1 ? t('menu.service') : t('menu.services')}
                      </div>
                    </div>

                    <div
                      className="rounded-full border border-white/60 p-2 text-slate-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] transition-transform duration-300"
                      style={{
                        background: `linear-gradient(180deg, ${hexToRgba('#ffffff', 0.66)}, ${hexToRgba(accent, 0.18)})`,
                      }}
                    >
                      <ChevronDown
                        className={[
                          'h-5 w-5 transition-transform duration-300',
                          isExpanded ? 'rotate-180' : '',
                        ].join(' ')}
                      />
                    </div>
                  </div>
                </button>

                <div
                  className={[
                    'grid transition-[grid-template-rows] duration-300 ease-out',
                    isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                  ].join(' ')}
                >
                  <div className="overflow-hidden">
                    <div className="space-y-2 px-3 pb-3">
                      {category.services.map((service) => (
                        <article
                          key={service.service_id}
                          className="relative overflow-hidden rounded-[22px] border border-white/80 bg-white/88 px-4 py-4 shadow-sm backdrop-blur-sm"
                        >
                          <div
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-x-4 top-0 h-8 rounded-b-[18px] bg-[linear-gradient(180deg,rgba(255,255,255,0.82),rgba(255,255,255,0.02))]"
                          />
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0 text-base font-semibold text-slate-900">
                              {service.service_name}
                            </div>
                            <div
                              className="shrink-0 rounded-full px-3 py-1 text-sm font-semibold"
                              style={{
                                backgroundColor: hexToRgba(accent, 0.12),
                                color: accent,
                              }}
                            >
                              {formatMoney(service.price)}
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MenuCategoriesPage;
